import React, { FC } from 'react';
import { CustomButton, CustomModal, If } from 'components';
import { ButtonType } from 'components/custom-button/CustomButton';
import { ErrorAlertIcon, NavCloseIcon } from 'assets/icons';
import { VoidFn } from 'types/baseTypes';
import { BulkActionState, JobRestrictionType } from '../types/bulkActions';

interface BulkActionConfirmationModalProps {
  open: boolean;
  onClose: VoidFn;
  onConfirm: VoidFn;
  bulkState: BulkActionState;
  isLoading?: boolean;
}

const BulkActionConfirmationModal: FC<BulkActionConfirmationModalProps> = ({
  open,
  onClose,
  onConfirm,
  bulkState,
  isLoading = false
}) => {
  const { notEditableCounts, possibleJobCount } = bulkState;

  const updatableJobsCount = possibleJobCount;

  const getTitle = () => {
    if (updatableJobsCount === 0) {
      return 'None of these jobs can be updated';
    }
    return 'Some selected jobs can`t be updated';
  };

  const getReasonText = () => {
    const reasons: React.ReactNode[] = [];

    // Helper function to build individual reason components
    const buildReasonComponent = (count: number, text: string) => {
      const isSingle = count === 1;
      return (
        <>
          <span className="font-bold">
            {count} {isSingle ? 'job' : 'jobs'}
          </span>{' '}
          {text}
        </>
      );
    };

    // Helper function to get descriptive text for different restriction types
    const getRestrictionText = (type: JobRestrictionType, count?: number) => {
      switch (type) {
        case JobRestrictionType.PAYRUN:
          return `${
            count === 1 ? 'has' : 'have'
          } already been reviewed (in accounting) or paid out (part of a completed pay run)`;
        case JobRestrictionType.DONE:
          return `${count === 1 ? 'is' : 'are'} marked as done, so you don't have permission to update them`;
        case JobRestrictionType.UNSCHEDULED:
          return `${count === 1 ? 'is' : 'are'} currently unscheduled (with no schedule date)`;
        case JobRestrictionType.NO_EDIT_PERMISSION:
          return `${count === 1 ? 'has' : 'have'} no permission to edit`;
        case JobRestrictionType.NO_DONE_PERMISSION:
          return `${count === 1 ? 'has' : 'have'} no permission to mark as done`;
        default:
          return '';
      }
    };

    if (updatableJobsCount === 0) {
      // When no jobs can be updated, provide a comprehensive summary
      const restrictionTypes: string[] = [];

      if (notEditableCounts.notEditableWithRespectToPayrunJobCount > 0) {
        restrictionTypes.push(getRestrictionText(JobRestrictionType.PAYRUN));
      }
      if (notEditableCounts.noPermissionToModifyDoneJobCount > 0) {
        restrictionTypes.push(getRestrictionText(JobRestrictionType.DONE));
      }
      if (notEditableCounts.noScheduleJobCount > 0) {
        restrictionTypes.push(getRestrictionText(JobRestrictionType.UNSCHEDULED));
      }
      if (notEditableCounts.noPermissionToEditJobCount > 0) {
        restrictionTypes.push(getRestrictionText(JobRestrictionType.NO_EDIT_PERMISSION));
      }
      if (notEditableCounts.noPermissionToMarkJobAsDoneCount > 0) {
        restrictionTypes.push(getRestrictionText(JobRestrictionType.NO_DONE_PERMISSION));
      }

      if (restrictionTypes.length > 0) {
        const formattedRestrictions =
          restrictionTypes.length > 1
            ? `${restrictionTypes.slice(0, -1).join(', ')} or ${restrictionTypes[restrictionTypes.length - 1]}`
            : restrictionTypes[0];

        reasons.push(` All selected jobs ${formattedRestrictions}.`);
      }
    } else {
      // When some jobs can be updated, provide specific counts for each restriction type
      const activeRestrictions: React.ReactNode[] = [];

      // Check for payrun restrictions
      if (notEditableCounts.notEditableWithRespectToPayrunJobCount > 0) {
        activeRestrictions.push(
          buildReasonComponent(
            notEditableCounts.notEditableWithRespectToPayrunJobCount,
            getRestrictionText(JobRestrictionType.PAYRUN, notEditableCounts.notEditableWithRespectToPayrunJobCount)
          )
        );
      }

      // Check for done job restrictions
      if (notEditableCounts.noPermissionToModifyDoneJobCount > 0) {
        activeRestrictions.push(
          buildReasonComponent(
            notEditableCounts.noPermissionToModifyDoneJobCount,
            getRestrictionText(JobRestrictionType.DONE, notEditableCounts.noPermissionToModifyDoneJobCount)
          )
        );
      }

      // Check for unscheduled job restrictions
      if (notEditableCounts.noScheduleJobCount > 0) {
        activeRestrictions.push(
          buildReasonComponent(
            notEditableCounts.noScheduleJobCount,
            getRestrictionText(JobRestrictionType.UNSCHEDULED, notEditableCounts.noScheduleJobCount)
          )
        );
      }

      // Check for edit permission restrictions (fallback case)
      if (notEditableCounts.noPermissionToEditJobCount > 0) {
        activeRestrictions.push(
          buildReasonComponent(
            notEditableCounts.noPermissionToEditJobCount,
            getRestrictionText(JobRestrictionType.NO_EDIT_PERMISSION, notEditableCounts.noPermissionToEditJobCount)
          )
        );
      }

      // Check for done permission restrictions (fallback case)
      if (notEditableCounts.noPermissionToMarkJobAsDoneCount > 0) {
        const noDonePermissionCount = notEditableCounts.noPermissionToMarkJobAsDoneCount;
        activeRestrictions.push(
          buildReasonComponent(
            noDonePermissionCount,
            getRestrictionText(JobRestrictionType.NO_DONE_PERMISSION, noDonePermissionCount)
          )
        );
      }

      reasons.push(...activeRestrictions);
    }

    return reasons;
  };

  const getPrimaryButtonText = () => {
    return `Continue with ${updatableJobsCount} ${updatableJobsCount === 1 ? 'job' : 'jobs'}`;
  };

  if (!open) return null;

  return (
    <CustomModal
      open={open}
      closeModal={onClose}
      backgroundColor="bg-primary"
      className="min-h-[240px]"
      width="md:w-full md:max-w-[421px]"
      enableOutsideClick={!isLoading}>
      <div className="relative flex w-full flex-col items-center px-4 py-[30px]">
        <div className="absolute top-5 right-5">
          <NavCloseIcon
            onClick={onClose}
            className={`shrink-0 ${isLoading ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
          />
        </div>

        <div className="mb-2 flex items-center justify-center">
          <ErrorAlertIcon className="h-[50px] w-[50px]" />
        </div>

        <div className="mb-3 text-center text-xl font-semibold text-headingGray">{getTitle()}</div>

        <div className={'mb-4 w-fit  p-2 text-center text-sbase font-normal text-primaryText'}>
          <div className="text-center text-sbase font-medium text-primaryText">
            {(() => {
              const reasons = getReasonText();
              const reasonsText = reasons?.map((reason, index) => (
                <React.Fragment key={index}>
                  {index > 0 && ', '}
                  {reason}
                </React.Fragment>
              ));

              if (updatableJobsCount > 0) {
                return (
                  <>
                    {reasonsText}, so your action will only apply to{' '}
                    <span className="font-bold">
                      {possibleJobCount} {possibleJobCount === 1 ? 'job' : 'jobs'}
                    </span>
                    .
                  </>
                );
              }

              return reasonsText;
            })()}
          </div>
        </div>

        <div className="mb-4 text-center text-sbase font-semibold text-primaryText">
          {updatableJobsCount > 0
            ? 'Would you like to continue and update the eligible jobs?'
            : 'To make updates, select jobs that are eligible.'}
        </div>

        <If condition={updatableJobsCount > 0}>
          <CustomButton
            id="bulk-action-confirm-button"
            type={ButtonType.PRIMARY}
            className="mb-4 h-[38px] w-full"
            onClick={onConfirm}
            isLoading={isLoading}
            disabled={isLoading}>
            {getPrimaryButtonText()}
          </CustomButton>
          <CustomButton
            id="bulk-action-cancel-button"
            type={ButtonType.SECONDARY}
            className="h-[38px] w-full"
            onClick={onClose}
            disabled={isLoading}>
            Cancel
          </CustomButton>
        </If>

        <If condition={updatableJobsCount === 0}>
          <CustomButton
            id="bulk-action-okay-button"
            type={ButtonType.PRIMARY}
            className="h-[38px] w-full"
            onClick={onClose}
            isLoading={isLoading}
            disabled={isLoading}>
            Back
          </CustomButton>
        </If>
      </div>
    </CustomModal>
  );
};

export default BulkActionConfirmationModal;
